import { navigateTo } from '#imports'
import type { RouteLocationNormalized } from 'vue-router'

import { useMarketBaseUrl } from '@backmarket/nuxt-module-marketplace/useMarketBaseUrl'
import type { RouteMiddleware } from 'nuxt/app'

import type { PaymentFlow } from '../config/flows'
import { type PaymentRouteParams, buildPaymentRoute } from '../config/routes'

import { useIsPaymentDomainEnabled } from './useIsPaymentDomainEnabled'

/**
 * Generate a route middleware that will redirect to the payment domain if the feature flag is enabled.
 *
 * Use it directly in the old payment page routes.
 * When the payment domain is enabled, the middleware will redirect to the new payment page (with a 302 redirect)
 * Otherwise, the middleware will let the page load normally.
 *
 * @example
 * meta: {
 *   middleware: getRedirectOnPaymentDomainMiddleware(
 *     PaymentFlow.INSURANCE_DUE_PAYMENT,
 *     (to) => ({ policyId: String(to.params.policyId) }),
 *   ),
 * },
 */
export function getRedirectOnPaymentDomainMiddleware<Flow extends PaymentFlow>(
  flow: Flow,
  resolveParams: (to: RouteLocationNormalized) => PaymentRouteParams<Flow>,
): RouteMiddleware {
  return (to) => {
    const isPaymentDomainEnabled = useIsPaymentDomainEnabled(flow)
    if (!isPaymentDomainEnabled.value) {
      return undefined
    }

    const { baseUrlWithLocale: paymentSiteUrl } = useMarketBaseUrl('payment')
    const paymentDomainUrl = `${paymentSiteUrl}/${buildPaymentRoute(flow, resolveParams(to))}`

    return navigateTo(paymentDomainUrl, { external: true })
  }
}
